import { DrawerBody } from '@app/components/ui/drawer/Drawer';
import { PrismicRichText } from '@prismicio/react';
import { Button } from 'mmds';
import { ProductInformationDocumentData } from 'prismicio-types';
import SizeGuide from './size-guide';
import { ReviewsResponseBody } from '@app/lib/reviewsio/methods/fetch-product-reviews';
import ProductReviews from './product-reviews';
import ProductRatings from './product-ratings';
import { SUPPORT_PATHS } from '../constants/support-paths';
import { VIEW_TYPES } from '../constants';
import { Language } from '@app/lib/store-context/Context';
import Linker from '@app/components/linker/linker';
import cn from 'clsx';
import s from './product-assistance.module.css';

interface ProductAssistanceDrawerContentProps {
    view: string;
    warranty: ProductInformationDocumentData | null;
    shipping: ProductInformationDocumentData | null;
    product: any; // TODO
    sizeGuide: ProductInformationDocumentData | null;
    reviews: ReviewsResponseBody | null;
    lang: Language;
    shopifyProductSizing: string | null;
}

const ProductAssistanceDrawerContent = ({
    warranty,
    shipping,
    view,
    product,
    sizeGuide,
    reviews,
    lang,
    shopifyProductSizing,
}: ProductAssistanceDrawerContentProps) => {
    const warrantyDescription = warranty?.warrantyDescription;
    const warrantyTitle = warranty?.warrantyTitle;
    const deliveryInfo = shipping?.deliveryInfo;

    // This is one way we can add custom styles to the html elements within PrismicRichText
    const richTextComponents = {
        paragraph: ({ children }) => (
            <p className={cn(s.textSecondary, 'mmds-copy-one')}>{children}</p>
        ),
        heading3: ({ children }) => (
            <h3 className={cn(s.sectionTitle, 'mmds-copy-one')}>{children}</h3>
        ),
    };

    const renderView = () => {
        switch (view) {
            case VIEW_TYPES.SIZE_GUIDE:
                return (
                    <div className={s.stack}>
                        <SizeGuide
                            product={product}
                            prismicSizeGuide={sizeGuide}
                            shopifyProductSizing={shopifyProductSizing}
                        />
                    </div>
                );
            case VIEW_TYPES.SHIPPING_RETURNS:
                return (
                    <div className={s.stack}>
                        {deliveryInfo?.map((item, index) => (
                            <div key={index} className={s.stack}>
                                <div
                                    className={s.stack}
                                    data-flow-space="small"
                                >
                                    <PrismicRichText
                                        field={item.title}
                                        components={richTextComponents}
                                    />
                                    <hr
                                        className="divider"
                                        data-color="steel"
                                    />
                                </div>
                                <div
                                    className={s.stack}
                                    data-flow-space="large"
                                >
                                    <div>
                                        <PrismicRichText
                                            field={item.description}
                                            components={richTextComponents}
                                        />
                                    </div>
                                    <Linker
                                        href={SUPPORT_PATHS[item.title[0].text]}
                                        title={`Go to ${item.title[0].text}`}
                                        lang={lang}
                                        target="_blank"
                                    >
                                        <Button
                                            label={`More ${item.title[0].text} info`}
                                            variant="ghost"
                                            icon="northeast"
                                        />
                                    </Linker>
                                    <hr
                                        className="divider"
                                        data-color="black"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                );
            case VIEW_TYPES.CRASH_REPLACEMENT:
                return (
                    <div className={s.stack}>
                        <div className={s.stack} data-flow-space="small">
                            <PrismicRichText
                                field={warrantyTitle}
                                components={richTextComponents}
                            />
                            <hr className="divider" data-color="steel" />
                        </div>
                        <div className={s.stack} data-flow-space="large">
                            <div className={s.stack}>
                                <PrismicRichText
                                    field={warrantyDescription}
                                    components={richTextComponents}
                                />
                            </div>
                            <Linker
                                href={SUPPORT_PATHS[view]}
                                title={`Go to ${view}`}
                                lang={lang}
                                target="_blank"
                            >
                                <Button
                                    label={`More ${view} info`}
                                    variant="ghost"
                                    icon="northeast"
                                />
                            </Linker>
                            <hr className="divider" data-color="black" />
                        </div>
                    </div>
                );
            case VIEW_TYPES.REVIEWS:
                if (!reviews) return null;
                return (
                    <div className={s.stack}>
                        <ProductRatings product={product} reviews={reviews} />
                        <hr className="divider" data-color="steel" />
                        <div className={cn(s.reviews, s.stack)}>
                            {reviews.reviews.map((review) => (
                                <ProductReviews
                                    key={review.id}
                                    review={review}
                                />
                            ))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return <DrawerBody>{renderView()}</DrawerBody>;
};

export default ProductAssistanceDrawerContent;
